import React from 'react';
import { Star, MoreVertical } from 'lucide-react';
import { BrandActionsMenu } from './BrandActionsMenu';
import { BrandStatusPopup } from './BrandStatusPopup';

interface BrandListRowProps {
  brand: any;
  isGlobalDarkMode: boolean;
  bookmarkedBrands: Set<string>;
  handleBookmarkToggle: (brandId: string, e: React.MouseEvent) => void;
  handleBrandClick: (brand: any) => void;
  handlePrefetch: (brandId: string) => void;
  getStatusInfo: (status: string) => { color: string; bgColor: string; icon: string; label: string };
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  handleDelete: (id: string) => void;
  handleAssignTeamMember: (id: string) => void;
  openStatusPopup: string | null;
  setOpenStatusPopup: (id: string | null) => void;
  handleStatusChange: (brandId: string, status: 'live' | 'private' | 'draft') => void;
}

export const BrandListRow: React.FC<BrandListRowProps> = ({
  brand,
  isGlobalDarkMode,
  bookmarkedBrands,
  handleBookmarkToggle,
  handleBrandClick,
  handlePrefetch,
  getStatusInfo,
  openMenuId,
  setOpenMenuId,
  handleDelete,
  handleAssignTeamMember,
  openStatusPopup,
  setOpenStatusPopup,
  handleStatusChange
}) => {
  const isBookmarked = bookmarkedBrands.has(brand.id);

  return (
    <div
      key={brand.id}
      className="rounded-2xl bg-card text-card-foreground border border-border shadow-sm p-6 transition-all duration-200 hover:shadow-md cursor-pointer"
      onMouseEnter={() => handlePrefetch(brand.id)}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.status-container') ||
          target.closest('button') ||
          openStatusPopup === brand.id ||
          openMenuId === brand.id) {
          return;
        }
        handleBrandClick(brand);
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Thumbnail / initial */}
          <div
            className="w-16 h-16 rounded-md flex items-center justify-center relative overflow-hidden"
            style={{
              backgroundColor: brand.sections[0]?.styles?.backgroundColor || '#3B82F6'
            }}
          >
            {brand.brandData?.brandThumbnail ? (
              <img
                src={brand.brandData.brandThumbnail}
                alt={brand.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.parentElement?.querySelector('.fallback-initial');
                  if (fallback) {
                    (fallback as HTMLElement).style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <span
              className={`text-white font-bold text-lg absolute inset-0 flex items-center justify-center ${brand.brandData?.brandThumbnail ? 'fallback-initial hidden' : ''
                }`}
            >
              {brand.name.charAt(0)}
            </span>
          </div>

          <div>
            <div className="flex items-center space-x-3 mb-1">
              <h4 className="text-xl font-semibold text-foreground">{brand.name}</h4>

              <button
                onClick={(e) => handleBookmarkToggle(brand.id, e)}
                className={`p-1 rounded-full transition-all duration-200 hover:scale-110 ${isBookmarked
                    ? 'text-yellow-500'
                    : 'text-muted-foreground hover:text-yellow-400'
                  }`}
                title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
              >
                <Star className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground">
              {brand.sections?.length || 0} sections • {brand.brandData?.industry || 'No industry'}
              {' • '}
              {new Date(brand.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="status-container">
            <BrandStatusPopup
              brand={brand}
              isGlobalDarkMode={isGlobalDarkMode}
              getStatusInfo={getStatusInfo}
              handleStatusChange={handleStatusChange}
              showStatusPopup={openStatusPopup}
              setShowStatusPopup={setOpenStatusPopup}
              isGridView={false}
            />
          </div>

          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === brand.id ? null : brand.id);
              }}
              className="p-2 transition-colors text-muted-foreground hover:text-foreground"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {openMenuId === brand.id && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(null);
                  }}
                />
                <div className="absolute top-8 right-0 z-40 rounded-lg border border-border bg-popover text-popover-foreground shadow-xl min-w-[180px] overflow-hidden">
                  <BrandActionsMenu
                    brandId={brand.id}
                    isGlobalDarkMode={isGlobalDarkMode}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    handleDelete={handleDelete}
                    handleAssignTeamMember={handleAssignTeamMember}
                    handleArchive={(id) => {
                      console.log('Archive brand:', id);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
